import { useState, useEffect, useRef } from 'react';

interface SafeImageProps {
  src: string;
  alt: string;
  size?: 'xs' | 'sm' | 'sm2' | 'md' | 'lg';
  className?: string;
}

const sizeClasses = {
  xs: 'w-8 h-8 rounded',
  sm: 'w-12 h-12 rounded-lg',
  sm2: 'w-10 h-10 rounded-lg',
  md: 'w-16 h-16 rounded-xl',
  lg: 'w-20 h-20 rounded-xl',
} as const;

/** One icon for all missing/failed images. Minimal vinyl disc. */
function PlaceholderIcon({ size }: { size: keyof typeof sizeClasses }) {
  const px = size === 'xs' ? 12 : size === 'sm' || size === 'sm2' ? 16 : 20;
  return (
    <svg width={px} height={px} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-stone-400 shrink-0" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/**
 * Image via CSS background only (no <img>). When missing or failed load, shows one classy icon for everyone.
 */
export default function SafeImage({ src, alt, size = 'sm', className = '' }: SafeImageProps) {
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const prevSrc = useRef('');

  const hasSrc = Boolean(src && String(src).trim());

  useEffect(() => {
    if (!hasSrc) {
      prevSrc.current = '';
      setLoadedUrl(null);
      return;
    }
    const s = String(src).trim();
    if (prevSrc.current === s) return;
    prevSrc.current = s;
    setLoadedUrl(null);

    const img = new Image();
    img.onload = () => setLoadedUrl(s);
    img.onerror = () => setLoadedUrl(null);
    img.src = s;

    return () => {
      img.onload = null;
      img.onerror = null;
      img.src = '';
    };
  }, [src, hasSrc]);

  const showPlaceholder = !hasSrc || !loadedUrl;
  const sizeClass = sizeClasses[size];

  return (
    <div
      className={`${sizeClass} shrink-0 overflow-hidden bg-stone-100 flex items-center justify-center ${
        showPlaceholder ? '' : 'bg-cover bg-center bg-no-repeat'
      } ${className}`.trim()}
      style={loadedUrl ? { backgroundImage: `url(${loadedUrl})` } : undefined}
      role="img"
      aria-label={showPlaceholder ? alt : alt}
    >
      {showPlaceholder && <PlaceholderIcon size={size} />}
    </div>
  );
}
